// src/App.jsx — v6 with React Router URL sync (Phase 2.12)
import { useState, useEffect, useCallback, useRef, Component, lazy, Suspense } from "react";
import { useNavigate as useRRNavigate, useLocation } from "react-router-dom";
import { updateDoc, doc } from "firebase/firestore";
import { sendEmailVerification } from "firebase/auth";
import { db } from "./firebase/config";
import { auth } from "./firebase/config";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import { AuthProvider } from "./hooks/useAuth";
import useAuth from "./hooks/useAuth";
import useTheme from "./hooks/useTheme";
import usePWA from "./hooks/usePWA";
import { GlobalCSS, Footer, WhatsAppFloat } from "./components/ui/UI";
import { ToastProvider } from "./components/ui/Toast";
import ScrollToTop from "./components/ui/ScrollToTop";
import CookieConsent from "./components/ui/CookieConsent";
import { BRAND } from "./config";
import Navbar      from "./components/ui/Navbar";
import Loader      from "./components/ui/Loader";
import ProfileCompleteScreen from "./components/ui/ProfileCompleteScreen";
import PortalIcon from "./components/ui/PortalIcon";
import { ensureNotificationPermissionForPush } from "./hooks/useNotifications";

import usePerformanceMonitor from "./hooks/usePerformanceMonitor";
import Home          from "./pages/Home";
import LandingPage    from "./pages/LandingPage";


// ── Lazy-loaded public pages (fixes code-split warnings) ────────────────────
const Services      = lazy(() => import("./pages/Services"));
const Bundles       = lazy(() => import("./pages/Bundles"));
const CourseCatalog = lazy(() => import("./pages/courses/CourseCatalog"));
const CourseDetail  = lazy(() => import("./pages/courses/CourseDetail"));
const Login         = lazy(() => import("./pages/Login"));
const BlogPage      = lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const NotFound      = lazy(() => import("./pages/NotFound"));
const ReviewPage    = lazy(() => import("./pages/ReviewPage"));
const CMSDashboard  = lazy(() => import("./pages/admin/CMSDashboard"));
const ClientPortal  = lazy(() => import("./pages/client/ClientPortal"));
const LMSApp        = lazy(() => import("./pages/lms/LMSApp"));
const StudentPortal = lazy(() => import("./pages/student/StudentPortal"));
const MaterialsPage = lazy(() => import("./pages/OtherPages").then(m => ({ default: m.Materials })));
const AboutPage     = lazy(() => import("./pages/OtherPages").then(m => ({ default: m.About })));
const GalleryPage   = lazy(() => import("./pages/OtherPages").then(m => ({ default: m.Gallery })));
const ContactPage   = lazy(() => import("./pages/OtherPages").then(m => ({ default: m.Contact })));
const PrivacyPolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.PrivacyPolicy })));
const TermsConditions = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.TermsConditions })));
const RefundPolicy  = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.RefundPolicy })));
const DigitalDeliveryPolicy = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.DigitalDeliveryPolicy })));
const SupportPolicyPage = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.SupportPolicy })));
const ChangelogPage   = lazy(() => import("./pages/Changelog"));
const SearchPage      = lazy(() => import("./pages/SearchPage"));
const BrandingStore   = lazy(() => import("./pages/BrandingStore"));
const MyBranding      = lazy(() => import("./pages/MyBranding"));
const BrandingInvoice = lazy(() => import("./pages/BrandingInvoice"));
const StorePage       = lazy(() => import("./pages/Store"));
const StoreItemDetail = lazy(() => import("./pages/StoreItemDetail"));

const PageLoader = () => (
  <div style={{ minHeight:"60vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14 }}>
    <div style={{ display:"flex", gap:7 }}>
      {[0,1,2].map(i=>(
        <div key={i} style={{
          width:9, height:9, borderRadius:"50%",
          background:"linear-gradient(135deg,#F59E0B,#F97316)",
          animation:`dgh-dot-pl 1.2s ease-in-out ${i*0.2}s infinite`,
        }}/>
      ))}
    </div>
    <style>{`@keyframes dgh-dot-pl{0%,80%,100%{transform:scale(.5);opacity:.3}40%{transform:scale(1);opacity:1}}`}</style>
  </div>
);

// ── Navbar height constant ───────────────────────────────────────────────────
export const NAVBAR_H = 64;

// ── Email Verification Gate ──────────────────────────────────────────────────
function EmailVerifyGate({ user, t }) {
  const [sent, setSent]     = useState(false);
  const [sending, setSending] = useState(false);
  const { logout }            = useAuth();

  const resend = async () => {
    if (sending || sent) return;
    setSending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      setSent(true);
    } catch (e) {
      alert("Email could not be sent. Please try again in a moment.");
    }
    setSending(false);
  };

  return (
    
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:t.bg, padding:20 }}>
      <div style={{ maxWidth:400, width:"100%", textAlign:"center" }}>
        <div style={{ width:70, height:70, borderRadius:20, background:`${t.accent}14`, color:t.accent, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}><PortalIcon name="mail" size={34} /></div>
        <h2 style={{ color:t.text, marginBottom:8, fontWeight:800 }}>Verify Your Email</h2>
        <p style={{ color:t.muted, lineHeight:1.7, marginBottom:24 }}>
          A verification link has been sent to <strong style={{ color:t.text }}>{user.email}</strong>.
          Please verify your email before accessing the portal.
        </p>

        {sent && (
          <div style={{ background:"rgba(16,185,129,.1)", border:"1px solid rgba(16,185,129,.3)", borderRadius:10, padding:"10px 14px", color:"#10B981", fontSize:".88rem", marginBottom:16 }}>
            Verification email resent! Please check your inbox.
          </div>
        )}

        {/* Gmail tip */}
        <div style={{ background:"rgba(99,102,241,.08)", border:"1px solid rgba(99,102,241,.2)", borderRadius:10, padding:"10px 14px", fontSize:".82rem", color:t.muted, marginBottom:16, lineHeight:1.6, textAlign:"left" }}>
          <PortalIcon name="info" size={14} style={{ marginRight:6 }} /><strong style={{ color:t.text }}>Gmail users:</strong> The email may arrive in the <strong>Promotions</strong> or <strong>Updates</strong> tab — check there too. Also check your spam folder.<br/>
          <span style={{ fontSize:".76rem" }}>From: noreply@dghelpmate-fd41c.firebaseapp.com</span>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <button onClick={() => window.location.reload()}
            style={{ padding:"13px", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", border:"none", borderRadius:10, fontWeight:800, fontSize:"1rem", cursor:"pointer" }}>
            Already Verified? Click to Refresh
          </button>
          <button onClick={resend} disabled={sending || sent}
            style={{ padding:"13px", background:t.bgCard, border:`1px solid ${t.border}`, color:sent ? t.muted : t.text, borderRadius:10, fontWeight:700, fontSize:".9rem", cursor:(sending || sent) ? "not-allowed" : "pointer", opacity:(sending || sent) ? .6 : 1 }}>
            {sending ? "Sending..." : sent ? "Email Sent!" : <><PortalIcon name="mail" size={15} style={{ marginRight:6 }} />Resend Verification Email</>}
          </button>
          <button onClick={logout}
            style={{ padding:"10px", background:"none", border:"none", color:t.muted, fontSize:".85rem", cursor:"pointer" }}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Error Boundary ───────────────────────────────────────────────────────────
function GooglePasswordPrompt({ t }) {
  const { user, profile, setPasswordForGoogleUser } = useAuth();
  const [form, setForm] = useState({ password:"", confirm:"" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const hasPassword = user?.providerData?.some((provider) => provider.providerId === "password");
  const skipKey = user?.uid ? `dgh_google_password_skip_${user.uid}` : "";

  useEffect(() => {
    if (!user || !profile?.googleSignIn || hasPassword || !skipKey) {
      setShow(false);
      return;
    }
    try { setShow(localStorage.getItem(skipKey) !== "1"); }
    catch { setShow(true); }
  }, [user, profile, hasPassword, skipKey]);

  const skip = () => {
    try { if (skipKey) localStorage.setItem(skipKey, "1"); } catch {}
    setShow(false);
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setSaving(true);
    try {
      await setPasswordForGoogleUser(form.password);
      setShow(false);
      setForm({ password:"", confirm:"" });
    } catch (err) {
      if (err.code === "auth/provider-already-linked" || err.code === "auth/credential-already-in-use") setShow(false);
      else setError("Password could not be added. Please try again.");
    }
    setSaving(false);
  };

  if (!show) return null;
  const inputStyle = { width:"100%", padding:"12px 14px", borderRadius:10, border:`1.5px solid ${t.border}`, background:t.bg, color:t.text, fontSize:"1rem", boxSizing:"border-box", marginTop:6 };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,.76)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(6px)" }}>
      <div style={{ width:"min(100%,430px)", background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:20, padding:"26px 22px", boxShadow:"0 28px 90px rgba(0,0,0,.42)" }}>
        <div style={{ width:46, height:46, borderRadius:15, background:`${t.gold}18`, color:t.gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <PortalIcon name="shield" size={23} />
        </div>
        <h3 style={{ color:t.text, textAlign:"center", margin:"0 0 8px", fontWeight:900 }}>Create a password too</h3>
        <p style={{ color:t.muted, textAlign:"center", fontSize:".88rem", lineHeight:1.6, margin:"0 0 18px" }}>
          You signed in with Google. Add a password so you can also login with email and password later.
        </p>
        <form onSubmit={save}>
          <label style={{ color:t.muted, fontSize:".78rem", fontWeight:800 }}>New Password</label>
          <input type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} placeholder="Min 6 chars" style={inputStyle} />
          <label style={{ color:t.muted, fontSize:".78rem", fontWeight:800, display:"block", marginTop:12 }}>Confirm Password</label>
          <input type="password" value={form.confirm} onChange={(e)=>setForm({...form,confirm:e.target.value})} placeholder="Repeat password" style={inputStyle} />
          {error && <div style={{ color:"#FCA5A5", fontSize:".82rem", marginTop:10 }}>{error}</div>}
          <button type="submit" disabled={saving} style={{ width:"100%", marginTop:16, padding:"13px", border:"none", borderRadius:10, background:saving ? t.border : "linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", fontWeight:900, cursor:saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving..." : "Save Password"}
          </button>
        </form>
        <button type="button" onClick={skip} disabled={saving} style={{ width:"100%", marginTop:12, padding:"10px", border:`1px solid ${t.border}`, borderRadius:10, background:"none", color:t.muted, cursor:saving ? "not-allowed" : "pointer" }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError:false, error:null }; }
  static getDerivedStateFromError(error) { return { hasError:true, error }; }
  componentDidCatch(error, info) { console.error("[ErrorBoundary]", error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    const t = this.props.t || {};
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:t.bg || "#0a0914", padding:20 }}>
        <div style={{ maxWidth:420, width:"100%", textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:18, background:"rgba(239,68,68,.12)", color:"#EF4444", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}><PortalIcon name="info" size={30} /></div>
          <h2 style={{ color:t.text || "#f0eeff", marginBottom:8, fontWeight:800 }}>Something Went Wrong</h2>
          <p style={{ color:t.muted || "#b8b4d8", lineHeight:1.7, marginBottom:24, fontSize:".9rem" }}>
            {this.state.error?.message || "An unexpected error occurred. Please refresh the page."}
          </p>
          <button onClick={() => window.location.reload()}
            style={{ padding:"12px 28px", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", border:"none", borderRadius:10, fontWeight:800, fontSize:"1rem", cursor:"pointer" }}>
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
}

// ── Google Analytics tracking ────────────────────────────────────────────────
export function trackPage(page) {
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_title: page,
      page_location: window.location.href,
      page_path: "/" + page,
    });
  }
}
export function trackEvent(action, category, label, value) {
  if (typeof window.gtag === "function") {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value,
    });
  }
}

// ── URL ↔ page-key mapping ───────────────────────────────────────────────────
const PAGE_TO_PATH = {
  home: "/", bundles: "/bundles", materials: "/materials",
  services: "/services", blog: "/blog", about: "/about", gallery: "/gallery",
  contact: "/contact", courses: "/courses", search: "/search",
  privacy: "/privacy-policy", terms: "/terms-and-conditions",
  refund: "/refund-policy", delivery: "/digital-delivery-policy",
  support: "/support", login: "/login", review: "/review",
  changelog: "/changelog", landing: "/landing", "course-detail": "/courses",
  "branding-store": "/branding-store",
  "my-branding":    "/my-branding",
  "my-account":     "/my-account",
  "student-portal": "/student-portal",
  "branding-invoice": "/branding-invoice",
  store: "/store",
};
const PATH_TO_PAGE = {
  "/": "home", "/bundles": "bundles", "/materials": "materials",
  "/services": "services", "/blog": "blog", "/about": "about", "/gallery": "gallery",
  "/contact": "contact", "/courses": "courses", "/search": "search",
  "/privacy-policy": "privacy", "/terms-and-conditions": "terms",
  "/refund-policy": "refund", "/digital-delivery-policy": "delivery",
  "/support": "support", "/login": "login", "/review": "review",
  "/changelog": "changelog", "/landing": "landing",
  "/dashboard": "home",
  "/branding-store": "branding-store",
  "/my-branding":    "my-branding",
  "/my-account":     "my-account",
  "/student-portal": "student-portal",
  "/branding-invoice": "branding-invoice",
  "/store": "store",
};

const isSafeInternalPath = (path) =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !/^[a-z][a-z0-9+.-]*:/i.test(path);

const getPostLoginRedirect = () => {
  const params = new URLSearchParams(window.location.search || "");
  const fromQuery = params.get("redirect") || params.get("returnTo") || params.get("next");
  if (isSafeInternalPath(fromQuery)) return fromQuery;

  try {
    const stored = localStorage.getItem("postLoginRedirect");
    if (isSafeInternalPath(stored)) return stored;
  } catch {}

  try {
    const pending = JSON.parse(sessionStorage.getItem("brandingPendingCustomize") || "null");
    if (isSafeInternalPath(pending?.returnPath)) return pending.returnPath;
  } catch {}

  return "";
};

const clearPostLoginRedirect = () => {
  try { localStorage.removeItem("postLoginRedirect"); } catch {}
  try { sessionStorage.removeItem("brandingPendingCustomize"); } catch {}
};

function InnerApp() {
  const { user, profile, loading, activeRole, switchRole, hasDualRole, logout } = useAuth();
  const { dark, t, toggleTheme } = useTheme();
  const { installPrompt, isInstalled, install } = usePWA();
  const rrNavigate = useRRNavigate();
  const location   = useLocation();
  const [pwaBarDismissed, setPwaBarDismissed] = useState(false);
  const showPwaBar = installPrompt && !isInstalled && !pwaBarDismissed;

  // ── Session timeout (item 27) ────────────────────────────────────────────────
  const [timeoutWarn, setTimeoutWarn] = useState(false);
  useSessionTimeout({
    enabled: !!user,
    onWarn:   useCallback(() => setTimeoutWarn(true), []),
    onLogout: useCallback(() => { setTimeoutWarn(false); logout(); }, [logout]),
  });
  const TimeoutOverlay = timeoutWarn ? (
    <div style={{ position:"fixed", inset:0, zIndex:99998, background:"rgba(0,0,0,.72)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:20, padding:"32px 28px", maxWidth:380, width:"100%", textAlign:"center" }}>
        <div style={{ width:56, height:56, borderRadius:16, background:`${t.gold}16`, color:t.gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}><PortalIcon name="clock" size={28} /></div>
        <h3 style={{ color:t.text, fontWeight:800, marginBottom:8, fontSize:"1.1rem" }}>Session Timeout Warning</h3>
        <p style={{ color:t.muted, fontSize:".88rem", lineHeight:1.7, marginBottom:22 }}>
          You have been inactive for 25 minutes. You will be automatically logged out in 5 minutes.
        </p>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => setTimeoutWarn(false)}
            style={{ flex:1, padding:"12px", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", border:"none", borderRadius:10, fontWeight:800, cursor:"pointer", fontFamily:"inherit", fontSize:"1rem" }}>
            <PortalIcon name="check" size={15} style={{ marginRight:6 }} />Stay Logged In
          </button>
          <button onClick={() => { setTimeoutWarn(false); logout(); }}
            style={{ padding:"12px 16px", background:"none", border:`1px solid ${t.border}`, borderRadius:10, color:t.muted, cursor:"pointer", fontFamily:"inherit" }}>
            Logout
          </button>
        </div>
      </div>
    </div>
  ) : null;
  const AuthOverlays = (
    <>
      <GooglePasswordPrompt t={t}/>
      {TimeoutOverlay}
    </>
  );

  const PUBLIC_PAGES = ["home","landing","services","bundles","materials","blog","about","gallery","contact","courses","course-detail","privacy","terms","refund","delivery","support","review","changelog","search","branding-store","my-branding","my-account","student-portal","branding-invoice","store","store-item"];
  const isStoreItemPath = (path) => {
    const parts = path.split("/").filter(Boolean);
    return parts[0] === "store" && parts.length >= 3;
  };
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (isStoreItemPath(path)) return "store-item";
    // URL takes priority over localStorage (supports direct links & refresh)
    const urlKey = PATH_TO_PAGE[path];
    if (urlKey) return urlKey;
    try {
      const saved = localStorage.getItem("dgh_page");
      return saved && PUBLIC_PAGES.includes(saved) && saved !== "store-item" ? saved : "home";
    } catch { return "home"; }
  };
  const [page, setPage] = useState(getInitialPage);
  const [courseId, setCourseId] = useState(() => {
    try { return localStorage.getItem("dgh_course_id") || null; } catch { return null; }
  });
  const [returnPage, setReturnPage] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      ensureNotificationPermissionForPush(user?.uid, { source: "app_stay" }).catch(() => {});
    }, 25000);
    return () => window.clearTimeout(timer);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    ensureNotificationPermissionForPush(user.uid, { source: "auth_state" }).catch(() => {});
  }, [user?.uid]);

  const navigate = (p) => {
    setPage(p);
    window.scrollTo(0, 0);
    trackPage(p);
    try { if (PUBLIC_PAGES.includes(p)) localStorage.setItem("dgh_page", p); } catch {}
    // Sync URL with page state
    const path = PAGE_TO_PATH[p] || "/";
    if (window.location.pathname !== path) rrNavigate(path);
  };

  const openCustomerAccount = (tab = "overview") => {
    const suffix = tab && tab !== "overview" ? `?tab=${encodeURIComponent(tab)}` : "";
    setPage("my-account");
    window.scrollTo(0, 0);
    trackPage("my-account");
    try { localStorage.setItem("dgh_page", "my-account"); } catch {}
    rrNavigate(`/my-account${suffix}`);
  };

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });
  useEffect(() => {
    const onAppNavigate = (e) => {
      const p = e.detail?.page;
      if (typeof p === "string") navigateRef.current(p);
    };
    window.addEventListener("dgh:navigate-page", onAppNavigate);
    return () => window.removeEventListener("dgh:navigate-page", onAppNavigate);
  }, []);

  // Sync page state when browser back/forward changes the URL
  useEffect(() => {
    if (isStoreItemPath(location.pathname)) {
      if (page !== "store-item") { setPage("store-item"); trackPage("store-item"); }
      return;
    }
    const urlKey = PATH_TO_PAGE[location.pathname];
    if (urlKey && urlKey !== page) {
      setPage(urlKey);
      trackPage(urlKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const goToLogin = () => {
    setReturnPage(page === "login" ? "home" : page);
    navigate("login");
  };

  const needsEmailVerification =
    !!(user && profile) &&
    (
      (profile.role === "pending" && profile.userType === "client" && !user.emailVerified) ||
      (profile.role === "student" && !profile.isAlsoClient && !user.emailVerified && !profile.googleSignIn && !profile.phoneSignIn)
    );

  useEffect(() => {
    if (!user || !profile) return;
    if (needsEmailVerification) return;

    const redirect = getPostLoginRedirect();

    if (profile.role === "admin") {
      clearPostLoginRedirect();
      if (redirect && redirect.startsWith("/dashboard")) rrNavigate(redirect, { replace: true });
      // Admin always goes to /dashboard via portal block — no further action needed.
      return;
    }

    if (redirect && isSafeInternalPath(redirect)) {
      clearPostLoginRedirect();
      const targetPage = PATH_TO_PAGE[redirect.split("?")[0]] || "branding-store";
      setPage(targetPage);
      rrNavigate(redirect, { replace: true });
    }
    // No stored redirect: login page will navigate("my-account") via the login page guard below.
  }, [user, profile, needsEmailVerification, rrNavigate]);

  // ✅ AUTO-APPROVAL: Email verify hone ke baad pending clients auto-approve karo
  useEffect(() => {
    const autoApprove = async () => {
      if (user && profile && user.emailVerified && profile.role === "pending" && profile.userType === "client") {
        try {
          await updateDoc(doc(db, "users", user.uid), {
            role:     "client",
            approved: true,
          });
        } catch(e) { console.error("Auto-approve error:", e); }
      }
    };
    autoApprove();
  }, [user, profile]);

  // ✅ User login ke baad return page pe bhejo
  useEffect(() => {
    if (user && profile && returnPage) {
      const portalRoles = ["admin", "client", "student", "pending"];
      if (!portalRoles.includes(profile.role)) {
        navigate(returnPage);
      }
      setReturnPage(null);
    }
  }, [user, profile]);

  const openCourse = (id) => {
    setCourseId(id);
    try { localStorage.setItem("dgh_course_id", id); } catch {}
    setTimeout(() => navigate("course-detail"), 0);
  };

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <GlobalCSS t={t}/>
        <Loader dark={dark}/>
      </>
    );
  }

  // ── Logged-in portals ──────────────────────────────────────────────────────
  const isBrandingInvoicePath = location.pathname.startsWith("/branding-invoice");
  const isCustomerAccountPath = location.pathname.startsWith("/my-account");
  const isStudentPortalPath   = location.pathname.startsWith("/student-portal");
  const isDashboardPath       = location.pathname.startsWith("/dashboard");

  // Portal-mode only fires for admin (always) + explicit portal paths + email gate.
  // All other paths (store, home, branding-store, etc.) render as public pages with navbar.
  const isPortalPath = isCustomerAccountPath || isDashboardPath || isStudentPortalPath;

  const renderClientPortal = ({ allowSwitch = false } = {}) => {
    if (!isCustomerAccountPath && !window.location.pathname.startsWith("/dashboard")) {
      window.history.replaceState({}, "", "/my-account");
    }
    return (
      <>
        <GlobalCSS t={t}/>
        {/* No public Navbar on /my-account — ClientPortal has its own portal header */}
        <ErrorBoundary t={t}>
          <Suspense fallback={<PageLoader/>}>
            <ClientPortal
              t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate}
              showSwitchBtn={allowSwitch}
              switchLabel="Go to Learning Panel"
              onSwitch={allowSwitch ? () => switchRole("student") : undefined}
            />
          </Suspense>
        </ErrorBoundary>
        {AuthOverlays}
      </>
    );
  };

  if (user && profile && (
    (profile.role === "admin" && !isBrandingInvoicePath) ||
    isPortalPath ||
    needsEmailVerification
  )) {

    // ✅ ADMIN — always admin panel
    if (profile.role === "admin") {
      if (!window.location.pathname.startsWith("/dashboard")) {
        window.history.replaceState({}, "", "/dashboard");
      }
      return <><GlobalCSS t={t}/><ErrorBoundary t={t}><Suspense fallback={<PageLoader/>}><CMSDashboard t={t}/></Suspense></ErrorBoundary>{AuthOverlays}</>;
    }

    // /dashboard is admin-only — redirect non-admin users to /my-account
    if (isDashboardPath) {
      if (!window.location.pathname.startsWith("/my-account")) window.history.replaceState({}, "", "/my-account");
      const canSwitchToStudent = profile.role === "student" || !!profile.isAlsoStudent;
      return renderClientPortal({ allowSwitch: canSwitchToStudent });
    }

    // /my-account — always the customer account experience, never StudentPortal
    if (isCustomerAccountPath) {
      const canSwitchToStudent = profile.role === "student" || !!profile.isAlsoStudent;
      return renderClientPortal({ allowSwitch: canSwitchToStudent });
    }

    // /student-portal — dedicated student/LMS portal; non-students redirected to /my-account
    if (isStudentPortalPath) {
      const canAccessStudentPortal = profile.role === "student" || !!profile.isAlsoStudent;
      if (!canAccessStudentPortal) {
        window.history.replaceState({}, "", "/my-account");
        return renderClientPortal({ allowSwitch: false });
      }
      if (!user.emailVerified && !profile.googleSignIn && !profile.phoneSignIn) {
        return <><GlobalCSS t={t}/><EmailVerifyGate user={user} t={t}/>{AuthOverlays}</>;
      }
      if (profile.googleSignIn && !profile.phone && !profile.profileCompleted) {
        return <><GlobalCSS t={t}/><ProfileCompleteScreen t={t} user={user} profile={profile}/>{AuthOverlays}</>;
      }
      const isDualStudentMode = hasDualRole(profile);
      return (
        <>
          <GlobalCSS t={t}/>
          <ErrorBoundary t={t}>
            <Suspense fallback={<PageLoader/>}>
              <StudentPortal
                t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate}
                showSwitchBtn={isDualStudentMode}
                switchLabel="Back to My Account"
                onSwitch={isDualStudentMode ? () => { switchRole("client"); navigate("my-account"); } : undefined}
              />
            </Suspense>
          </ErrorBoundary>
          {AuthOverlays}
        </>
      );
    }

    // ✅ OLD pending accounts (legacy) — nudge to verify email; approval is automatic now
    if (profile.role === "pending" && profile.userType === "client") {
      if (!user.emailVerified) {
        return <><GlobalCSS t={t}/><EmailVerifyGate user={user} t={t}/>{AuthOverlays}</>;
      }
      // Email verified but role not yet flipped — show a short holding screen
      return (
        <>
          <GlobalCSS t={t}/>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:t.bg, flexDirection:"column", gap:16, padding:20, textAlign:"center" }}>
            <div style={{ width:64, height:64, borderRadius:18, background:"rgba(16,185,129,.12)", color:"#10B981", display:"flex", alignItems:"center", justifyContent:"center" }}><PortalIcon name="check" size={30} /></div>
            <h2 style={{ color:t.text }}>Almost Ready…</h2>
            <p style={{ color:t.muted, maxWidth:420, lineHeight:1.65 }}>
              Your email is verified. We are finishing setup — please refresh in a few seconds.
            </p>
            <button onClick={() => window.location.reload()}
              style={{ padding:"11px 24px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", fontWeight:800 }}>
              Refresh Now
            </button>
          </div>
          {AuthOverlays}
        </>
      );
    }

    // ✅ STUDENT (pure — no client access)
    if (profile.role === "student" && !profile.isAlsoClient) {
      if (!user.emailVerified && !profile.googleSignIn && !profile.phoneSignIn) {
        return <><GlobalCSS t={t}/><EmailVerifyGate user={user} t={t}/>{AuthOverlays}</>;
      }
      if (profile.googleSignIn && !profile.phone && !profile.profileCompleted) {
        return <><GlobalCSS t={t}/><ProfileCompleteScreen t={t} user={user} profile={profile}/>{AuthOverlays}</>;
      }
      return <><GlobalCSS t={t}/><ErrorBoundary t={t}><Suspense fallback={<PageLoader/>}><StudentPortal t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate} showSwitchBtn={false}/></Suspense></ErrorBoundary>{AuthOverlays}</>;
    }

    // ✅ CLIENT (pure — no student access)
    if (profile.role === "client" && !profile.isAlsoStudent) {
      return renderClientPortal({ allowSwitch: false });
    }

    // ✅ DUAL ROLE — client jo student bhi hai, ya student jo client bhi hai
    const isDual = hasDualRole(profile);
    if (isDual) {
      const currentRole = activeRole || profile.role;

      if (currentRole === "client") {
        return renderClientPortal({ allowSwitch: true });
      }

      if (currentRole === "student") {
        if (!user.emailVerified && !profile.googleSignIn && !profile.phoneSignIn) {
          return <><GlobalCSS t={t}/><EmailVerifyGate user={user} t={t}/>{AuthOverlays}</>;
        }
        if (profile.googleSignIn && !profile.phone && !profile.profileCompleted) {
          return <><GlobalCSS t={t}/><ProfileCompleteScreen t={t} user={user} profile={profile}/>{AuthOverlays}</>;
        }
        return (
          <>
            <GlobalCSS t={t}/>
            <ErrorBoundary t={t}>
              <Suspense fallback={<PageLoader/>}>
                <StudentPortal
                  t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate}
                  showSwitchBtn={true}
                  switchLabel="Switch to Client Mode"
                  onSwitch={() => switchRole("client")}
                />
              </Suspense>
            </ErrorBoundary>
            {AuthOverlays}
          </>
        );
      }
    }

    // Fallback
    if (profile.role === "client") {
      return renderClientPortal({ allowSwitch: false });
    }
    if (profile.role === "student") {
      return <><GlobalCSS t={t}/><Suspense fallback={<PageLoader/>}><StudentPortal t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate} showSwitchBtn={false}/></Suspense>{AuthOverlays}</>;
    }
  }

  // ── Login page ─────────────────────────────────────────────────────────────
  // Agar user logged-in hai (profile load nahi hua edge case) toh login page mat dikhaao
  if (page === "login" || page === "client-login" || page === "student-login") {
    if (user) { navigate("my-account"); return <><GlobalCSS t={t}/><Loader dark={dark}/></>; }
    const defaultMode = page === "client-login" ? "client" : page === "student-login" ? "student" : "login";
    return <><GlobalCSS t={t}/><Suspense fallback={<PageLoader/>}><Login t={t} onBack={()=>navigate("home")} defaultMode={defaultMode}/></Suspense></>;
  }

  // ── Public pages ───────────────────────────────────────────────────────────
  const renderPage = () => {
    switch (page) {
      case "home":          return <Home setPage={navigate} t={t}/>;
      case "landing":        return <LandingPage />;
      case "services":      return <Suspense fallback={<PageLoader/>}><Services t={t}/></Suspense>;
      case "bundles":       return <Suspense fallback={<PageLoader/>}><Bundles t={t}/></Suspense>;
      case "materials":     return <Suspense fallback={<PageLoader/>}><MaterialsPage t={t}/></Suspense>;
      case "blog":          return <Suspense fallback={<PageLoader/>}><BlogPage t={t}/></Suspense>;
      case "about":         return <Suspense fallback={<PageLoader/>}><AboutPage t={t} setPage={navigate}/></Suspense>;
      case "gallery":       return <Suspense fallback={<PageLoader/>}><GalleryPage t={t}/></Suspense>;
      case "contact":       return <Suspense fallback={<PageLoader/>}><ContactPage t={t}/></Suspense>;
      case "courses":       return user
        ? <Suspense fallback={<PageLoader/>}><LMSApp openCourse={openCourse} setPage={navigate} t={t}/></Suspense>
        : <Suspense fallback={<PageLoader/>}><CourseCatalog openCourse={openCourse} setPage={navigate} t={t}/></Suspense>;
      case "course-detail": return <Suspense fallback={<PageLoader/>}><CourseDetail courseId={courseId} setPage={navigate} t={t}/></Suspense>;
      case "privacy":       return <Suspense fallback={<PageLoader/>}><PrivacyPolicy t={t}/></Suspense>;
      case "terms":         return <Suspense fallback={<PageLoader/>}><TermsConditions t={t}/></Suspense>;
      case "refund":        return <Suspense fallback={<PageLoader/>}><RefundPolicy t={t}/></Suspense>;
      case "delivery":      return <Suspense fallback={<PageLoader/>}><DigitalDeliveryPolicy t={t}/></Suspense>;
      case "support":       return <Suspense fallback={<PageLoader/>}><SupportPolicyPage t={t}/></Suspense>;
      case "review":        return <Suspense fallback={<PageLoader/>}><ReviewPage t={t}/></Suspense>;
      case "changelog":     return <Suspense fallback={<PageLoader/>}><ChangelogPage t={t}/></Suspense>;
      case "search":         return <Suspense fallback={<PageLoader/>}><SearchPage t={t} setPage={navigate} openCourse={openCourse}/></Suspense>;
      case "branding-store": return <Suspense fallback={<PageLoader/>}><BrandingStore t={t} setPage={navigate}/></Suspense>;
      case "my-branding":    return <Suspense fallback={<PageLoader/>}><MyBranding t={t} setPage={navigate}/></Suspense>;
      case "my-account":
        if (!user) {
          try { localStorage.setItem("postLoginRedirect", "/my-account"); } catch {}
          return <Suspense fallback={<PageLoader/>}><Login t={t} onBack={()=>navigate("home")} defaultMode="login"/></Suspense>;
        }
        return <Suspense fallback={<PageLoader/>}><ClientPortal t={t} dark={dark} toggleTheme={toggleTheme} setPage={navigate}/></Suspense>;
      case "student-portal":
        if (!user) {
          try { localStorage.setItem("postLoginRedirect", "/student-portal"); } catch {}
          return <Suspense fallback={<PageLoader/>}><Login t={t} onBack={()=>navigate("home")} defaultMode="login"/></Suspense>;
        }
        // Logged-in: the portal block intercepts before renderPage — this is a safety fallback
        return <Suspense fallback={<PageLoader/>}><Login t={t} onBack={()=>navigate("home")} defaultMode="login"/></Suspense>;
      case "branding-invoice": return <Suspense fallback={<PageLoader/>}><BrandingInvoice t={t} setPage={navigate}/></Suspense>;
      case "store":          return <Suspense fallback={<PageLoader/>}><StorePage t={t} setPage={navigate}/></Suspense>;
      case "store-item":     return <Suspense fallback={<PageLoader/>}><StoreItemDetail t={t} setPage={navigate}/></Suspense>;
      default:               return <Suspense fallback={<PageLoader/>}><NotFound setPage={navigate} t={t}/></Suspense>;
    }
  };

  return (
    <div style={{ background:t.bg, minHeight:"100vh" }}>
      <GlobalCSS t={t}/>
      {/* PWA install banner */}
      {showPwaBar && (
        <div style={{ 
          position:"fixed", 
          bottom:0, 
          left:0, 
          right:0, 
          zIndex:10000, 
          background:`linear-gradient(135deg,${t.bgCard},${t.bgCard2 || t.bgCard})`, 
          borderTop:`1px solid ${t.border}`, 
          padding:"12px 20px", 
          display:"flex", 
          alignItems:"center", 
          justifyContent:"space-between", 
          gap:12, 
          flexWrap:"wrap",
          boxShadow: dark ? "0 -4px 20px rgba(0,0,0,0.3)" : "0 -8px 26px rgba(15,23,42,0.10)",
          backdropFilter: "blur(10px)"
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ width:38, height:38, borderRadius:10, background:"rgba(245,158,11,.14)", color:"#F59E0B", display:"flex", alignItems:"center", justifyContent:"center" }}><PortalIcon name="phone" size={20} /></span>
            <div>
              <div style={{ fontWeight:700, color:t.text, fontSize:".9rem" }}>Install DG HelpMate App</div>
              <div style={{ fontSize:".76rem", color:t.muted }}>Fast access - works offline too</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={install}
              style={{ 
                padding:"8px 18px", 
                background:"linear-gradient(135deg,#F59E0B,#F97316)", 
                color:"#070B14", 
                border:"none", 
                borderRadius:9, 
                fontWeight:800, 
                fontSize:".85rem", 
                cursor:"pointer", 
                fontFamily:"inherit",
                boxShadow: "0 2px 8px rgba(245,158,11,0.4)",
                transition: "transform 0.2s, box-shadow 0.2s"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(245,158,11,0.6)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(245,158,11,0.4)";
              }}
            >
              Install
            </button>
            <button onClick={()=>setPwaBarDismissed(true)}
              style={{ 
                padding:"8px 12px", 
                background:"none", 
                border:`1px solid ${t.border}`, 
                borderRadius:9, 
                color:t.muted, 
                cursor:"pointer", 
                fontFamily:"inherit", 
                fontSize:".85rem",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = t.bgCard2 || "rgba(255,255,255,0.1)";
                e.currentTarget.style.borderColor = t.border;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.borderColor = t.border;
              }}
            >
              <PortalIcon name="x" size={16} />
            </button>
          </div>
        </div>
      )}

            <Navbar page={page} setPage={navigate} dark={dark} toggleTheme={toggleTheme} t={t} onPortalClick={goToLogin} onAccountClick={openCustomerAccount} user={user} profile={profile} onLogout={logout}/>
      <main style={{ paddingTop: NAVBAR_H, paddingBottom: showPwaBar ? "86px" : 0 }}>{renderPage()}</main>
      <Footer setPage={navigate} t={t}/>
      <WhatsAppFloat elevated={showPwaBar}/>
      <ScrollToTop elevated={showPwaBar} t={t}/>
      <CookieConsent t={t} />
    </div>
  );
}

function PerformanceInstrumentation() {
  usePerformanceMonitor({
    trackCoreWebVitals: true,
    trackPageLoad: true,
    trackResources: true,
    trackErrors: true,
  });
  return null;
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <InnerApp />
        <PerformanceInstrumentation />
      </AuthProvider>
    </ToastProvider>
  );
}
